import { reset, change } from 'redux-form'
import { postRequest, urlBuilder } from 'tfs.front-end-utils/requests'

import { SeatTradeSearchResultsFormName } from '../../config/forms'
import staticData from '../../config/staticData'

import { customTourOperatorParameters } from '../../utils/tourOperators'
import { filterFieldErrors, filterFlightErrors } from '../../utils/errors'
import { buildSeatTradeNotification } from '../../utils/notifications'
import formatErrors from '../../utils/errors/formatErrors'
import getAuthenticationHeaders from '../../utils/requests/generateAuthorizationHeaders'

import {
  v1receiveSeatTrade,
  v1addSeatTradeNotification,
} from '../../actions/seatTrade'
import v1throwError from '../../actions/errors/v1throwError'
import { V1_REQUEST_SEAT_TRADE } from '../../actions/seatTrade/v1requestSeatTrade'
import { v1setContextMenu } from '../../actions/contextMenu'
import v1requestCompleted from '../../actions/requests/v1requestCompleted'
import v1receiveNotifications from '../../actions/notifications/v1receiveNotifications'

/**
 * Currently seat trade does not have a schema to standardize
 * the data. This is due to the actual unique data of a seat trade
 * being:
 * - flightId: "AMSFAO20190223HV5355"
 * - availableSeatCount: -8
 *
 * All the other data if a copy of the actual flight data.
 * To avoid over complicating the seat trade schemas we've
 * decided to first simplify our flights state management
 * by normalizing it. Once this is done the actual seat trade
 * schema will make more sense as we can get additional information
 * from the flights store while keeping the seat trade store clean.
 */
export default ({ dispatch }) => next => async action => {
  next(action)

  if (action.type !== V1_REQUEST_SEAT_TRADE) {
    return
  }

  const {
    endpoints: {
      allotments: { url, headers },
    },
  } = staticData

  const fixedUrl = url.endsWith('/') ? url : `${url}/`
  const requestUrl = urlBuilder(
    `${fixedUrl}seattrade/trade`,
    customTourOperatorParameters()
  )

  try {
    const {
      seatTrades: requestSeatTrades,
      tourOperator: {
        value: { code },
      },
    } = action.data
    const authHeaders = await getAuthenticationHeaders(headers)

    const requestBody = {
      seatTrades: requestSeatTrades.map(({ seats, ...rest }) => {
        return {
          ...rest,
          numberOfSeats: parseInt(seats),
          receivingTourOperatorCode: code,
        }
      }),
    }

    const response = await postRequest(requestUrl, {
      headers: { ...headers, ...authHeaders },
      body: JSON.stringify(requestBody),
    })

    dispatch(v1requestCompleted(response))

    const { seatTrades = [], errors = [] } = response

    seatTrades.forEach(seatTrade => {
      dispatch(
        v1addSeatTradeNotification([buildSeatTradeNotification(seatTrade)])
      )
    })

    dispatch(v1setContextMenu(''))
    dispatch(
      v1receiveSeatTrade({
        seatTrades,
        errors: formatErrors(filterFieldErrors(errors)),
      })
    )

    if (!errors.length) {
      dispatch(
        v1receiveNotifications([
          buildSeatTradeNotification({}, 'all_sale_of_seats_has_succeeded'),
        ])
      )
    }

    /**
     * Reset and re-submit the failed seat trade values.
     * The reason for resetting the entire form is due to
     * redux-form not allowing us to reset one specific field.
     *
     * To ensure we still show the filled in values on the failed
     * seat trades we'll manually fire the actions to set the values
     * in the form gain.
     */
    dispatch(reset(SeatTradeSearchResultsFormName))
    filterFlightErrors(errors).forEach(({ data = {} }) => {
      dispatch(
        change(SeatTradeSearchResultsFormName, data.flightId, {
          [data.flightId]: requestSeatTrades.find(
            seatTrade => seatTrade.flightId === data.flightId
          ).seats,
        })
      )
    })
  } catch (error) {
    dispatch(v1throwError(error))
  }
}
